import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";

import api, { getApiErrorMessage } from "./api";
import {
  getAccessToken,
  setAccessToken,
  removeAccessToken,
} from "../utils/Storage";


interface User {
  id: number;
  name: string;
  username: string;
  email: string;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string
  ) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  changePassword: (oldPassword: string, newPassword: string) => Promise<any>;
  forgotPassword: (username: string, email: string) => Promise<any>;
  setPassword: (
    email: string,
    token: string,
    newPassword: string
  ) => Promise<any>;
}

const Auth = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(Auth);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const token = getAccessToken();
  useEffect(() => {
    // Check if user is logged in on page load
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const fetchCurrentUser = async () => {
        try {
          const response = await api.get("/v1/profile/");
          setCurrentUser(response.data);
          setIsAuthenticated(true);
          setLoading(false);
        } catch (err) {
          console.error("Error fetching current user:", err);
          setError(err as string);
          // Clear invalid token
          removeAccessToken();
          delete api.defaults.headers.common["Authorization"];
          setCurrentUser(null);
          setIsAuthenticated(false);
          setLoading(false);
        }
      };

      // Try to load user data using the token
      if (currentUser === null) {
        setLoading(true);
        setError(null);
        fetchCurrentUser();
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/v1/login/", { username, password });

      setAccessToken(response.data.token);
      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.data.token}`;

      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "Failed to login. Please check your credentials and try again."
        )
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(
    async (username: string, email: string, password: string) => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.post("/v1/register/", {
          username,
          email,
          password,
          password2: password,
        });

        setAccessToken(response.data.token);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.token}`;

        setCurrentUser(response.data.user);
        setIsAuthenticated(true);
      } catch (err) {
        setError(getApiErrorMessage(err, "Registration failed. Please try again."));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback(() => {
    removeAccessToken();
    localStorage.removeItem("user");
    delete api.defaults.headers.common["Authorization"];
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      setLoading(true);
      setError(null);

      return await api
        .post("/v1/change_pass/", { oldPassword, newPassword })
        .then((response) => {
          return response;
        })
        .catch((err) => {
          setError(
            getApiErrorMessage(err, "Failed to change password. Please try again.")
          );
          throw err;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  const forgotPassword = useCallback(async (username: string, email: string) => {
    setLoading(true);
    setError(null);

    return await api
      .post("/v1/reset_pass", { username, email })
      .then((response) => {
        return response;
      })
      .catch((err) => {
        setError(
          getApiErrorMessage(err, "Failed to change password. Please try again.")
        );
        throw err;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const setPassword = useCallback(
    async (email: string, token: string, newPassword: string) => {
      setLoading(true);
      setError(null);

      return await api
        .post("/v1/reset_pass/confirm/", { email, token, newPassword })
        .then((response) => {
          return response;
        })
        .catch((err) => {
          setError(
            getApiErrorMessage(err, "Failed to change password. Please try again.")
          );
          throw err;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    changePassword,
    forgotPassword,
    setPassword,
    isAuthenticated,
  };

  return <Auth.Provider value={value}>{children}</Auth.Provider>;
};
